// Adding a language is config: drop src/assets/i18n/<code>.json and add an entry here.
export interface AppLanguage {
  code: string;
  locale: string;
  /** The language's own name, in its own script — what a reader scans the menu for. */
  label: string;
  /**
   * The English name, shown beside the native one.
   *
   * A reader who does not read the script cannot pick the language out of a list of thirty,
   * and an administrator choosing which languages to offer is very often not a speaker of
   * them. Both menus therefore show both names.
   */
  englishLabel: string;
  /** Groups the menu. A flat list of thirty is not a menu, it is a search problem. */
  region: LanguageRegion;
  dir: "ltr" | "rtl";
}

export type LanguageRegion = "india" | "international";

export const LANGUAGE_REGIONS: readonly LanguageRegion[] = ["india", "international"] as const;

/**
 * Every language the portal ships.
 *
 * **Shipping a language is not the same as having translated it.** A bundle that does not exist
 * falls back to English key by key, so an entry here with no `assets/i18n/<code>.json` behind it
 * renders an English portal with a native-language name on the switcher — which is a lie the
 * reader only discovers after switching. `language-coverage.ts` is the guard against that: it
 * records what each bundle actually covers, the Languages settings screen shows it, and a
 * tenant can decide what to offer on real figures rather than on this list's length.
 *
 * Locale tags are chosen for `Intl`, not for the translation file. `mni`, `brx`, `kha` and
 * `lus` have no CLDR data of their own, so `Intl` resolves them to a neighbour — verified as
 * producing sane dates and currency rather than throwing, which is all this field is for.
 *
 * The North-East India set (Assamese, Manipuri, Bodo, Khasi, Mizo, Nepali, Bengali) is
 * deliberately first-class rather than filed under "other Indian languages": those are the
 * languages of the region the product is being shown in, and a switcher that offers Tamil but
 * not Assamese answers the wrong question there.
 */
export const APP_LANGUAGES: readonly AppLanguage[] = [
  { code: "en", locale: "en-IN", label: "English", englishLabel: "English", region: "india", dir: "ltr" },
  { code: "hi", locale: "hi-IN", label: "हिन्दी", englishLabel: "Hindi", region: "india", dir: "ltr" },
  { code: "bn", locale: "bn-IN", label: "বাংলা", englishLabel: "Bengali", region: "india", dir: "ltr" },
  { code: "as", locale: "as-IN", label: "অসমীয়া", englishLabel: "Assamese", region: "india", dir: "ltr" },
  { code: "mni", locale: "mni-IN", label: "ꯃꯤꯇꯩꯂꯣꯟ", englishLabel: "Manipuri", region: "india", dir: "ltr" },
  { code: "brx", locale: "brx-IN", label: "बर’", englishLabel: "Bodo", region: "india", dir: "ltr" },
  { code: "kha", locale: "kha-IN", label: "Khasi", englishLabel: "Khasi", region: "india", dir: "ltr" },
  { code: "lus", locale: "lus-IN", label: "Mizo ṭawng", englishLabel: "Mizo", region: "india", dir: "ltr" },
  { code: "ne", locale: "ne-IN", label: "नेपाली", englishLabel: "Nepali", region: "india", dir: "ltr" },
  { code: "mr", locale: "mr-IN", label: "मराठी", englishLabel: "Marathi", region: "india", dir: "ltr" },
  { code: "ta", locale: "ta-IN", label: "தமிழ்", englishLabel: "Tamil", region: "india", dir: "ltr" },
  { code: "te", locale: "te-IN", label: "తెలుగు", englishLabel: "Telugu", region: "india", dir: "ltr" },
  { code: "kn", locale: "kn-IN", label: "ಕನ್ನಡ", englishLabel: "Kannada", region: "india", dir: "ltr" },
  { code: "ml", locale: "ml-IN", label: "മലയാളം", englishLabel: "Malayalam", region: "india", dir: "ltr" },
  { code: "gu", locale: "gu-IN", label: "ગુજરાતી", englishLabel: "Gujarati", region: "india", dir: "ltr" },
  { code: "pa", locale: "pa-IN", label: "ਪੰਜਾਬੀ", englishLabel: "Punjabi", region: "india", dir: "ltr" },
  { code: "or", locale: "or-IN", label: "ଓଡ଼ିଆ", englishLabel: "Odia", region: "india", dir: "ltr" },
  { code: "ur", locale: "ur-IN", label: "اردو", englishLabel: "Urdu", region: "india", dir: "rtl" },

  { code: "ar", locale: "ar-AE", label: "العربية", englishLabel: "Arabic", region: "international", dir: "rtl" },
  { code: "es", locale: "es-ES", label: "Español", englishLabel: "Spanish", region: "international", dir: "ltr" },
  { code: "fr", locale: "fr-FR", label: "Français", englishLabel: "French", region: "international", dir: "ltr" },
  { code: "pt", locale: "pt-BR", label: "Português", englishLabel: "Portuguese", region: "international", dir: "ltr" },
  { code: "de", locale: "de-DE", label: "Deutsch", englishLabel: "German", region: "international", dir: "ltr" },
  { code: "ru", locale: "ru-RU", label: "Русский", englishLabel: "Russian", region: "international", dir: "ltr" },
  { code: "zh", locale: "zh-CN", label: "简体中文", englishLabel: "Chinese (Simplified)", region: "international", dir: "ltr" },
  { code: "ja", locale: "ja-JP", label: "日本語", englishLabel: "Japanese", region: "international", dir: "ltr" },
  { code: "id", locale: "id-ID", label: "Bahasa Indonesia", englishLabel: "Indonesian", region: "international", dir: "ltr" },
  { code: "vi", locale: "vi-VN", label: "Tiếng Việt", englishLabel: "Vietnamese", region: "international", dir: "ltr" },
  { code: "sw", locale: "sw-KE", label: "Kiswahili", englishLabel: "Swahili", region: "international", dir: "ltr" },
  { code: "fil", locale: "fil-PH", label: "Filipino", englishLabel: "Filipino", region: "international", dir: "ltr" }
] as const;

export const DEFAULT_LANGUAGE = "en";

export const LANGUAGE_STORAGE_KEY = "recolearn.lang";

// Overrides live in assets/i18n/terms/<lang>.<preset>.json, merged over the base bundle.
// Keys match `docs/design_handoff/recolearn/data.js` presets exactly. Note it is
// `business`, not `corporate` — README.md and DESIGN-SYSTEM.md say "corporate", but the
// prototype data is authoritative and uses `business`.
export type IndustryPreset =
  | "standard"
  | "school"
  | "coaching"
  | "fitness"
  | "business"
  | "homeschool"
  | "healthcare";

export const INDUSTRY_PRESETS: readonly IndustryPreset[] = [
  "standard",
  "school",
  "coaching",
  "fitness",
  "business",
  "homeschool",
  "healthcare"
] as const;

/**
 * The tenant's industry arrives as `account.category`, which holds two different vocabularies.
 *
 * Tenants created through the onboarding wizard store the preset id itself — the wizard collects
 * it in canonical form and `POST /onboardings` drops it straight into the free-text column. Older
 * tenants hold a display label from the legacy account form ("Coaching Institute", "Health
 * Wellness and Fitness"), which is also the vocabulary the API's `INDUSTRY_USER_TAG` map is keyed
 * on. Matching the raw value against the preset ids therefore resolves the first group and
 * silently misses the second.
 *
 * College and University map to `school`: the presets describe *vocabulary*, not sector, and all
 * three want learner/teacher. `Event` has no preset of its own and stays standard rather than
 * borrowing another industry's words.
 */
const CATEGORY_TO_PRESET: Readonly<Record<string, IndustryPreset>> = {
  "coaching institute": "coaching",
  school: "school",
  college: "school",
  university: "school",
  "health wellness and fitness": "fitness",
  corporate: "business",
  business: "business",
  event: "standard"
};

/**
 * Resolves a stored `account.category` to the terminology preset it should load.
 *
 * Unknown values resolve to `standard`: a tenant who typed their own industry gets neutral
 * vocabulary, which is the correct answer rather than a guessed one.
 */
export function resolveIndustryPreset(raw: string | null | undefined): IndustryPreset {
  if (raw === null || raw === undefined) {
    return "standard";
  }
  const value = raw.replace(/^"|"$/g, "").trim().toLowerCase();
  if (value === "") {
    return "standard";
  }
  const asPreset = INDUSTRY_PRESETS.find((p) => p === value);
  if (asPreset !== undefined) {
    return asPreset;
  }
  return CATEGORY_TO_PRESET[value] ?? "standard";
}
